import type {
  PeerData,
  FileTransfer,
  TransferStatus,
} from '../types';

/**
 * WebRTC service for peer-to-peer file transfer using native RTCPeerConnection
 * Manual signaling: one peer creates an offer, the other answers
 */
export class WebRTCService {
  private peerConnection: RTCPeerConnection | null = null;
  private dataChannel: RTCDataChannel | null = null;
  private isConnected: boolean = false;
  private localPeerId: string;
  private remotePeerData: PeerData | null = null;
  private onTransferProgress?: (transfer: FileTransfer) => void;
  private onTransferComplete?: (transfer: FileTransfer) => void;
  private onTransferError?: (error: string) => void;
  private onPeerConnected?: (peer: PeerData) => void;
  private onPeerDisconnected?: () => void;
  private onOfferCreated?: (offer: RTCSessionDescriptionInit) => void;
  private onAnswerCreated?: (answer: RTCSessionDescriptionInit) => void;
  private onFileReceived?: (file: File, blob: Blob) => void;
  private onMessageReceived?: (htmlContent: string, from: string) => void;

  // File receiving state
  private receivingFile: {
    metadata: any;
    chunks: ArrayBuffer[];
  } | null = null;

  constructor() {
    this.localPeerId = this.generatePeerId();
  }

  /**
   * Generate unique peer ID
   */
  private generatePeerId(): string {
    return `peer_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Create RTCPeerConnection with proper configuration
   */
  private createPeerConnection(): RTCPeerConnection {
    const config: RTCConfiguration = {
      iceServers: [
        { urls: 'stun:stun.l.google.com:19302' },
        { urls: 'stun:stun1.l.google.com:19302' },
        { urls: 'stun:global.stun.twilio.com:3478' }
      ],
      iceCandidatePoolSize: 10
    };

    const pc = new RTCPeerConnection(config);

    // Log connection state changes
    pc.onconnectionstatechange = () => {
      console.log('Connection state:', pc.connectionState);
      if (pc.connectionState === 'connected') {
        this.isConnected = true;
        if (this.remotePeerData) {
          this.remotePeerData.connected = true;
          this.onPeerConnected?.(this.remotePeerData);
        }
      } else if (pc.connectionState === 'failed' || pc.connectionState === 'disconnected' || pc.connectionState === 'closed') {
        this.isConnected = false;
        this.onPeerDisconnected?.();
      }
    };

    pc.oniceconnectionstatechange = () => {
      console.log('ICE connection state:', pc.iceConnectionState);
    };

    return pc;
  }

  /**
   * Setup data channel for file transfer
   */
  private setupDataChannel(channel: RTCDataChannel): void {
    this.dataChannel = channel;
    channel.binaryType = 'arraybuffer';

    channel.onopen = () => {
      console.log('✅ Data channel opened');
      this.isConnected = true;
      if (this.remotePeerData) {
        this.remotePeerData.connected = true;
        this.onPeerConnected?.(this.remotePeerData);
      }
    };

    channel.onclose = () => {
      console.log('🔌 Data channel closed');
      this.isConnected = false;
      this.onPeerDisconnected?.();
    };

    channel.onerror = (error) => {
      console.error('❌ Data channel error:', error);
      this.onTransferError?.('Data channel error');
    };

    channel.onmessage = (event) => {
      this.handleIncomingData(event.data);
    };
  }

  /**
   * Handle incoming data from peer
   */
  private handleIncomingData(data: string | ArrayBuffer): void {
    // Check if it's metadata (string) or file chunk (ArrayBuffer)
    if (typeof data === 'string') {
      try {
        const parsed = JSON.parse(data);
        if (parsed.type === 'file-metadata') {
          console.log('📥 Receiving file:', parsed.name);
          this.receivingFile = {
            metadata: parsed,
            chunks: []
          };
        } else if (parsed.type === 'text-message') {
          console.log('💬 Message received from peer');
          this.onMessageReceived?.(parsed.content, this.remotePeerData?.name || 'Unknown');
        }
      } catch (err) {
        console.error('Failed to parse metadata:', err);
      }
    } else if (data instanceof ArrayBuffer) {
      // File chunk
      if (!this.receivingFile) {
        console.warn('Received chunk without metadata');
        return;
      }

      this.receivingFile.chunks.push(data);

      const receivedSize = this.receivingFile.chunks.reduce(
        (acc, chunk) => acc + chunk.byteLength,
        0
      );
      const progress = (receivedSize / this.receivingFile.metadata.size) * 100;

      // Update progress
      this.onTransferProgress?.({
        id: `${this.localPeerId}_receive`,
        file: new File([], this.receivingFile.metadata.name),
        peer: this.remotePeerData || { id: 'remote', name: 'Remote' },
        status: 'transferring' as TransferStatus,
        progress,
        bytesTransferred: receivedSize,
        totalBytes: this.receivingFile.metadata.size,
        startTime: Date.now(),
      });

      // Check if transfer complete
      if (receivedSize >= this.receivingFile.metadata.size) {
        const blob = new Blob(this.receivingFile.chunks, {
          type: this.receivingFile.metadata.mimeType
        });
        const file = new File([blob], this.receivingFile.metadata.name, {
          type: this.receivingFile.metadata.mimeType
        });

        console.log('✅ File received:', this.receivingFile.metadata.name);

        // Notify UI that file was received (don't auto-download)
        this.onFileReceived?.(file, blob);

        this.onTransferComplete?.({
          id: `${this.localPeerId}_receive`,
          file,
          peer: this.remotePeerData || { id: 'remote', name: 'Remote' },
          status: 'completed' as TransferStatus,
          progress: 100,
          bytesTransferred: this.receivingFile.metadata.size,
          totalBytes: this.receivingFile.metadata.size,
          startTime: Date.now(),
        });

        this.receivingFile = null;
      }
    }
  }

  /**
   * Set remote peer data
   */
  setRemotePeerData(peerData: PeerData): void {
    this.remotePeerData = peerData;
  }

  /**
   * Create an offer to start a connection (host side)
   */
  async createOffer(): Promise<void> {
    try {
      // Clean up existing connection
      if (this.peerConnection) {
        this.peerConnection.close();
      }

      // remotePeerData should be set before calling createOffer
      if (!this.remotePeerData) {
        this.remotePeerData = { id: 'remote', name: 'Unknown Peer', connected: false };
      }
      this.peerConnection = this.createPeerConnection();

      // Create data channel (initiator creates it)
      const channel = this.peerConnection.createDataChannel('fileTransfer', {
        ordered: true
      });
      this.setupDataChannel(channel);

      // Create offer
      const offer = await this.peerConnection.createOffer();
      await this.peerConnection.setLocalDescription(offer);

      // Wait for ICE gathering to complete (trickle: false behavior)
      await this.waitForIceGathering();

      // Notify that offer is ready
      if (this.peerConnection.localDescription) {
        console.log('📤 Offer created');
        this.onOfferCreated?.(this.peerConnection.localDescription.toJSON());
      }
    } catch (err) {
      console.error('Error creating offer:', err);
      this.onTransferError?.('Failed to create offer');
    }
  }

  /**
   * Wait for ICE candidate gathering to complete
   */
  private waitForIceGathering(): Promise<void> {
    return new Promise((resolve) => {
      if (!this.peerConnection) {
        resolve();
        return;
      }

      if (this.peerConnection.iceGatheringState === 'complete') {
        resolve();
        return;
      }

      const checkState = () => {
        if (this.peerConnection?.iceGatheringState === 'complete') {
          this.peerConnection.removeEventListener('icegatheringstatechange', checkState);
          resolve();
        }
      };

      this.peerConnection.addEventListener('icegatheringstatechange', checkState);

      // Timeout after 3 seconds for faster connection
      setTimeout(() => {
        if (this.peerConnection) {
          this.peerConnection.removeEventListener('icegatheringstatechange', checkState);
        }
        resolve();
      }, 3000);
    });
  }

  /**
   * Answer an offer (responder side)
   */
  async answerOffer(offer: RTCSessionDescriptionInit): Promise<void> {
    try {
      // Clean up existing connection
      if (this.peerConnection) {
        this.peerConnection.close();
      }

      // remotePeerData should be set before calling answerOffer
      if (!this.remotePeerData) {
        this.remotePeerData = { id: 'remote', name: 'Unknown Peer', connected: false };
      }
      this.peerConnection = this.createPeerConnection();

      // Setup handler for incoming data channel
      this.peerConnection.ondatachannel = (event) => {
        console.log('📥 Data channel received');
        this.setupDataChannel(event.channel);
      };

      // Set remote offer
      await this.peerConnection.setRemoteDescription(new RTCSessionDescription(offer));

      // Create answer
      const answer = await this.peerConnection.createAnswer();
      await this.peerConnection.setLocalDescription(answer);

      // Wait for ICE gathering
      await this.waitForIceGathering();

      // Notify that answer is ready
      if (this.peerConnection.localDescription) {
        console.log('📤 Answer created');
        this.onAnswerCreated?.(this.peerConnection.localDescription.toJSON());
      }
    } catch (err) {
      console.error('Error answering offer:', err);
      this.onTransferError?.('Failed to answer offer: ' + (err instanceof Error ? err.message : 'Unknown error'));
    }
  }

  /**
   * Complete the connection with the answer (initiator side)
   */
  async processAnswer(answer: RTCSessionDescriptionInit): Promise<void> {
    if (!this.peerConnection) {
      this.onTransferError?.('No active peer connection');
      return;
    }

    try {
      await this.peerConnection.setRemoteDescription(new RTCSessionDescription(answer));
      console.log('✅ Answer processed, waiting for connection...');
    } catch (err) {
      console.error('Error processing answer:', err);
      this.onTransferError?.('Failed to process answer: ' + (err instanceof Error ? err.message : 'Unknown error'));
    }
  }


  /**
   * Send file to connected peer
   */
  async sendFile(file: File): Promise<void> {
    if (!this.dataChannel || this.dataChannel.readyState !== 'open') {
      throw new Error('Data channel not open. Please wait for connection.');
    }

    const CHUNK_SIZE = 16384; // 16KB chunks
    const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
    let currentChunk = 0;

    console.log(`📤 Sending file: ${file.name} (${file.size} bytes)`);

    // Send file metadata first
    const metadata = {
      type: 'file-metadata',
      name: file.name,
      size: file.size,
      mimeType: file.type,
    };
    this.dataChannel.send(JSON.stringify(metadata));

    // Read and send file in chunks
    const reader = new FileReader();

    const sendNextChunk = () => {
      const start = currentChunk * CHUNK_SIZE;
      const end = Math.min(start + CHUNK_SIZE, file.size);
      const blob = file.slice(start, end);

      reader.readAsArrayBuffer(blob);
    };

    reader.onload = () => {
      if (this.dataChannel && reader.result) {
        this.dataChannel.send(reader.result as ArrayBuffer);
        currentChunk++;

        const progress = (currentChunk / totalChunks) * 100;
        this.onTransferProgress?.({
          id: this.localPeerId,
          file,
          peer: this.remotePeerData || { id: 'remote', name: 'Remote' },
          status: 'transferring' as TransferStatus,
          progress,
          bytesTransferred: Math.min(currentChunk * CHUNK_SIZE, file.size),
          totalBytes: file.size,
          startTime: Date.now(),
        });

        if (currentChunk < totalChunks) {
          sendNextChunk();
        } else {
          // Transfer complete
          console.log('✅ File sent successfully');
          this.onTransferComplete?.({
            id: this.localPeerId,
            file,
            peer: this.remotePeerData || { id: 'remote', name: 'Remote' },
            status: 'completed' as TransferStatus,
            progress: 100,
            bytesTransferred: file.size,
            totalBytes: file.size,
            startTime: Date.now(),
          });
        }
      }
    };

    reader.onerror = () => {
      console.error('Error reading file');
      this.onTransferError?.('Error reading file');
    };

    sendNextChunk();
  }

  /**
   * Send text message to connected peer
   */
  async sendMessage(htmlContent: string): Promise<void> {
    if (!this.dataChannel || this.dataChannel.readyState !== 'open') {
      throw new Error('Data channel not open. Please wait for connection.');
    }

    console.log('💬 Sending text message to peer');

    // Send message
    const message = {
      type: 'text-message',
      content: htmlContent,
    };
    this.dataChannel.send(JSON.stringify(message));
  }

  /**
   * Set callback handlers
   */
  setCallbacks(callbacks: {
    onTransferProgress?: (transfer: FileTransfer) => void;
    onTransferComplete?: (transfer: FileTransfer) => void;
    onTransferError?: (error: string) => void;
    onPeerConnected?: (peer: PeerData) => void;
    onPeerDisconnected?: () => void;
    onOfferCreated?: (offer: RTCSessionDescriptionInit) => void;
    onAnswerCreated?: (answer: RTCSessionDescriptionInit) => void;
    onFileReceived?: (file: File, blob: Blob) => void;
    onMessageReceived?: (htmlContent: string, from: string) => void;
  }): void {
    this.onTransferProgress = callbacks.onTransferProgress;
    this.onTransferComplete = callbacks.onTransferComplete;
    this.onTransferError = callbacks.onTransferError;
    this.onPeerConnected = callbacks.onPeerConnected;
    this.onPeerDisconnected = callbacks.onPeerDisconnected;
    this.onOfferCreated = callbacks.onOfferCreated;
    this.onAnswerCreated = callbacks.onAnswerCreated;
    this.onFileReceived = callbacks.onFileReceived;
    this.onMessageReceived = callbacks.onMessageReceived;
  }

  /**
   * Clean up peer connection
   */
  cleanup(): void {
    if (this.dataChannel) {
      this.dataChannel.close();
      this.dataChannel = null;
    }
    if (this.peerConnection) {
      this.peerConnection.close();
      this.peerConnection = null;
    }
    this.isConnected = false;
    this.remotePeerData = null;
    this.receivingFile = null;
  }

  /**
   * Get connection status
   */
  getIsConnected(): boolean {
    return this.isConnected;
  }

  /**
   * Get remote peer data
   */
  getRemotePeer(): PeerData | null {
    return this.remotePeerData;
  }

  /**
   * Get peer connection (for debugging)
   */
  getPeerConnection(): RTCPeerConnection | null {
    return this.peerConnection;
  }
}

export const webrtcService = new WebRTCService();
